import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { ExpandCircleDownIcon, Spinner, Tag } from '@mercai/clever'

import { SelectStatusProps } from './select-status.type'

import styles from './select-status.module.css'

export const SelectStatus = ({
  onChange,
  options,
  status = '',
  size = 'medium',
  excludeOptions = [],
  allowEmpty = false,
}: SelectStatusProps) => {
  const { t } = useTranslation('componentsSelectStatus')
  const [isOpen, setIsOpen] = useState(false)
  const [popperPosition, setPopperPosition] = useState({ top: 0, left: 0 })

  const [isLoading, setIsLoading] = useState(false)

  const buttonRef = useRef<HTMLDivElement | null>(null)
  const popperRef = useRef<HTMLDivElement | null>(null)

  const actualOption = useMemo(() => {
    if (!status) {
      return { label: t('empty'), value: '', variant: 'basic' as const }
    }
    return options?.find((option) => option.value === status)
  }, [status, options, t])

  const togglePopper = () => {
    if (!onChange || isLoading) {
      return true
    }

    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPopperPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      })
    }

    setIsOpen((prev) => !prev)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      popperRef.current &&
      !popperRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false)
    }
  }

  const onHandleChange = async (value: string) => {
    setIsLoading(true)
    await onChange?.(value)
    setIsLoading(false)
    setIsOpen(false)
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className={styles.core}>
      <div
        ref={buttonRef}
        onClick={togglePopper}
        className={styles['status-button']}
        style={{ cursor: onChange && !isLoading ? 'pointer' : 'default' }}
      >
        <Tag
          variant={actualOption?.variant || 'basic'}
          model="light"
          size={size}
        >
          {actualOption?.label}
          {onChange &&
            (isLoading ? (
              <Spinner />
            ) : (
              <ExpandCircleDownIcon
                style={{
                  fill: `var(--${actualOption?.variant || 'basic'}-800)`,
                }}
              />
            ))}
        </Tag>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={popperRef}
            className={styles.popper}
            style={{
              position: 'fixed',
              top: `${popperPosition.top}px`,
              left: `${popperPosition.left}px`,
              zIndex: 1000,
            }}
          >
            <ul>
              {allowEmpty && (
                <li
                  key=""
                  className={!status ? styles.active : ''}
                  onClick={() => onHandleChange('')}
                >
                  {t('empty')}
                </li>
              )}
              {options
                ?.filter(
                  (val) =>
                    !excludeOptions.find((exclude) => exclude === val.value),
                )
                .map(({ label, value }) => (
                  <li
                    key={value}
                    className={
                      value === actualOption?.value ? styles.active : ''
                    }
                    onClick={() => onHandleChange(value)}
                  >
                    {label}
                  </li>
                ))}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  )
}
